import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type {
  Tables,
  TablesUpdate,
} from '@/integrations/supabase/types';

// Row type for the circuits table
export type Circuit = Tables<'circuits'>;
// Update type for the circuits table (partial by definition)
export type CircuitUpdate = TablesUpdate<'circuits'>;

/**
 * Fetch all circuits that belong to a specific floor plan.
 * This returns the full row, including panel_id & panel_slot.
 */
export async function getCircuits(floorPlanId: string): Promise<Circuit[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .select('*')
    .eq('floor_plan_id', floorPlanId);

  if (error) throw error;
  return (data ?? []) as Circuit[];
}

/**
 * Update a circuit by ID. Accepts any subset of circuit columns.
 * Returns the updated row.
 */
export async function updateCircuit(
  id: string,
  updates: CircuitUpdate,
): Promise<Circuit> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Circuit;
}

/**
 * Rename a circuit
 */
export async function renameCircuit(circuitId: string, newName: string): Promise<Circuit> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from("circuits")
    .update({ name: newName })
    .eq("id", circuitId)
    .select()
    .single();
  
  if (error) throw error;
  if (!data) throw new Error(`Circuit with id ${circuitId} not found`);
  return data as Circuit;
}

/**
 * Assign a circuit to a specific module output.
 * This will update the circuit's module_output_id.
 */
export async function assignToModuleOutput(
  circuitId: string,
  moduleOutputId: string,
): Promise<Circuit> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .update({ module_output_id: moduleOutputId } as any)
    .eq('id', circuitId)
    .select()
    .single();

  if (error) throw error;
  return data as Circuit;
}

/**
 * Unassign a circuit from its module output by setting
 * module_output_id to null.
 */
export async function unassignFromModuleOutput(
  circuitId: string,
): Promise<Circuit> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .update({ module_output_id: null } as any)
    .eq('id', circuitId)
    .select()
    .single();

  if (error) throw error;
  return data as Circuit;
}

/**
 * Get all circuits assigned to a specific module output.
 */
export async function getCircuitsByModuleOutput(
  moduleOutputId: string,
): Promise<Circuit[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .select('*')
    .eq('module_output_id', moduleOutputId);

  if (error) throw error;
  return (data ?? []) as Circuit[];
}

/**
 * Get all unassigned circuits (those without a module_output_id).
 */
export async function getUnassignedCircuits(
  floorPlanId: string,
): Promise<Circuit[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('circuits')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .is('module_output_id', null);

  if (error) throw error;
  return (data ?? []) as Circuit[];
} 
